import { TableHead, TableHeader, TableRow } from '@talentmesh/ui/components/table';
import { cn } from '@talentmesh/ui/lib/utils';

import { type MRT_ColumnVirtualizer, type MRT_RowData, type MRT_TableInstance } from '../../types';
import { type TableTheadProps } from '../../types/mrt-ui-props';
import { parseFromValuesOrFunc } from '../../utils/utils';
import { MRT_ToolbarAlertBanner } from '../toolbar/MRT_ToolbarAlertBanner';
import classes from './MRT_TableHead.module.css';
import { MRT_TableHeadRow } from './MRT_TableHeadRow';

interface Props<TData extends MRT_RowData> extends TableTheadProps {
    columnVirtualizer?: MRT_ColumnVirtualizer;
    table: MRT_TableInstance<TData>;
}

export const MRT_TableHead = <TData extends MRT_RowData>({ columnVirtualizer, table, ...rest }: Props<TData>) => {
    const {
        getHeaderGroups,
        getSelectedRowModel,
        getState,
        options: { enableStickyHeader, layoutMode, mantineTableHeadProps, positionToolbarAlertBanner },
        refs: { tableHeadRef },
    } = table;
    const { isFullScreen, showAlertBanner } = getState();

    const tableHeadProps = {
        ...parseFromValuesOrFunc(mantineTableHeadProps, {
            table,
        }),
        ...rest,
    };

    const stickyHeader = enableStickyHeader || isFullScreen;

    return (
        <TableHeader
            {...tableHeadProps}
            className={cn(
                classes.root,
                layoutMode?.startsWith('grid') ? classes['root-grid'] : classes['root-table-row-group'],
                stickyHeader && classes['root-sticky'],
                stickyHeader && layoutMode?.startsWith('grid') && 'sticky top-0 z-10',
                stickyHeader && !layoutMode?.startsWith('grid') && 'relative',
                tableHeadProps?.className,
            )}
            ref={(ref: HTMLTableSectionElement | null) => {
                tableHeadRef.current = ref;
                if (tableHeadProps?.ref) {
                    // @ts-ignore
                    tableHeadProps.ref.current = ref;
                }
            }}
        >
            {positionToolbarAlertBanner === 'head-overlay' &&
            (showAlertBanner || getSelectedRowModel().rows.length > 0) ? (
                <TableRow className={cn(classes['banner-tr'], layoutMode?.startsWith('grid') && classes.grid)}>
                    <TableHead
                        className={cn(classes['banner-th'], layoutMode?.startsWith('grid') && classes.grid)}
                        colSpan={table.getVisibleLeafColumns().length}
                    >
                        <MRT_ToolbarAlertBanner table={table} />
                    </TableHead>
                </TableRow>
            ) : (
                getHeaderGroups().map((headerGroup) => (
                    <MRT_TableHeadRow
                        columnVirtualizer={columnVirtualizer}
                        headerGroup={headerGroup as any}
                        key={headerGroup.id}
                        table={table}
                    />
                ))
            )}
        </TableHeader>
    );
};
